import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export type ToastStatus = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastProps {
  title: string;
  description?: string;
  status: ToastStatus;
  duration?: number;
  onClose?: () => void;
  /** Ação secundária dentro do toast — usada, por ex., pelo botão "Desfazer". */
  action?: ToastAction;
  /** Posição empilhada entre toasts simultâneos, evita sobrepor uns aos outros. */
  offsetIndex?: number;
}

const Toast: React.FC<ToastProps> = ({
  title,
  description,
  status,
  duration = 5000,
  onClose,
  action,
  offsetIndex = 0
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const statusStyles = {
    success: 'bg-nk-success',
    error: 'bg-nk-error',
    warning: 'bg-nk-warn',
    info: 'bg-nk-info'
  };

  if (!isVisible) return null;

  return createPortal(
    <div
      role="alert"
      style={{ bottom: `${16 + offsetIndex * 84}px` }}
      className={`fixed right-4 p-4 rounded-md shadow-lg z-[120] ${statusStyles[status]} text-white min-w-[300px] max-w-md animate-slide-in`}
    >
      <div className="flex justify-between items-start">
        <div>
          <h3 className="font-bold text-lg">{title}</h3>
          {description && <p className="mt-1 text-sm opacity-90">{description}</p>}
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            onClose?.();
          }}
          className="p-1 hover:bg-white/20 rounded-full transition-colors"
          aria-label="Fechar"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {action && (
        <div className="mt-2 flex justify-end">
          <button
            onClick={() => {
              action.onClick();
              setIsVisible(false);
              onClose?.();
            }}
            className="text-sm font-semibold underline underline-offset-2 hover:opacity-80 transition-opacity"
          >
            {action.label}
          </button>
        </div>
      )}
    </div>,
    document.body
  );
};

// Hook para gerenciar múltiplos toasts
export const useToast = () => {
  const [toasts, setToasts] = useState<Array<ToastProps & { id: string }>>([]);

  const show = (props: ToastProps) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { ...props, id }]);
    return id;
  };

  const close = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const ToastContainer = () => (
    <>
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          {...toast}
          onClose={() => close(toast.id)}
        />
      ))}
    </>
  );

  return { show, close, ToastContainer };
};

export default Toast; 